import type { LiveServerMessage, Session } from "@google/genai"

import type { VoiceTicket } from "@/lib/types"
import { Microphone, VoicePlayer, toBase64 } from "@/lib/voice/audio"
import { createSilenceDetector } from "@/lib/voice/silence"

/**
 * One spoken answer, start to finish, over Google's Live API.
 *
 *   connecting -> asking -> listening -> finishing -> done
 *
 * The voice reads the question (`asking`), then the microphone is opened to
 * Google and the candidate answers (`listening`) for as long as they like -
 * push-to-talk, so a pause to think never ends the answer. Pressing Finish
 * sends the end of the turn; the voice says "Thank you" and the connection
 * closes (`done`). What Google wrote down is handed back as text, to go into
 * the answer box where the candidate can check it before it is scored.
 *
 * **Hands-free** (`TurnOptions.handsFree`): the same turn, but the page also
 * listens for the end of the answer itself (`voice/silence.ts`) and calls
 * `onSilence` - the caller then finishes the turn exactly as if Finish had
 * been pressed. The speaker and microphone stay open afterwards
 * (`keepAudio`), so the next question can be read without another click;
 * between turns nothing is sent, because no listener is attached.
 *
 * The browser talks to Google directly with a one-use token the server issued
 * (`audio/live.py` explains why, and what is locked into it). The SDK (about
 * 75 kB compressed) is a separate download, so nobody who never sees the voice
 * button pays for it - but it is fetched as soon as the button is on screen
 * (`preloadVoice`), not after the click, where it used to add a download to the
 * wait for the interviewer's first word.
 *
 * **Timing.** Each step of the wait is reported through `onTiming`, and the
 * Interview page writes the breakdown to the browser console - microphone,
 * token, SDK, connection, first sound - so a slow start can be pinned on the
 * step that caused it instead of guessed at.
 */

export type TurnPhase = "connecting" | "asking" | "listening" | "finishing" | "done"

let sdk: Promise<typeof import("@google/genai")> | null = null

/** Fetch Google's SDK ahead of the click. Safe to call often: fetched once. */
export function preloadVoice(): Promise<typeof import("@google/genai")> {
  sdk ??= import("@google/genai").catch((reason: unknown) => {
    // A failed download is retried on the next call rather than remembered.
    sdk = null
    throw reason
  })
  return sdk
}

/** The steps between the click and the first word, in order. */
export type VoiceStep = "microphone" | "token" | "sdk" | "connected" | "first sound"

export interface TurnHandlers {
  onPhase: (phase: TurnPhase) => void
  /** Everything the candidate has said so far, as Google transcribed it. */
  onAnswer: (text: string) => void
  /** The connection failed or closed early. The turn is over. */
  onError: (message: string) => void
  /** Hands-free only: the candidate has stopped talking. Finish the turn now. */
  onSilence?: () => void
  /** A step of the start-up is done. For measuring, never for behaviour. */
  onTiming?: (step: VoiceStep) => void
}

export interface TurnOptions {
  /** Detect the end of the answer from silence, and report it via `onSilence`. */
  handsFree?: boolean
  /** Leave the speaker and microphone open when the turn ends, for the next one. */
  keepAudio?: boolean
}

/** Hands-free: quiet this long after speaking ends the answer. */
export const HANDS_FREE_SILENCE_MS = 3000
/** Hands-free: nothing said for this long stops listening. */
const HANDS_FREE_NO_SPEECH_MS = 20000

export interface SpokenTurn {
  /** The candidate has finished speaking. Resolves with the transcript. */
  finish: () => Promise<{ text: string; seconds: number }>
  /** Abandon the turn: close everything, keep nothing. */
  cancel: () => void
}

/** Created synchronously in the click, so the browser lets the page make sound. */
export interface TurnAudio {
  player: VoicePlayer
  microphone: Microphone
}

export function prepareAudio(): TurnAudio {
  const audio = { player: new VoicePlayer(), microphone: new Microphone() }
  void audio.player.unlock()
  return audio
}

/**
 * Ask for the microphone before anything else. A refusal then costs nothing:
 * no token is requested and nothing counts against the daily allowance.
 */
export async function openMicrophone(audio: TurnAudio): Promise<void> {
  try {
    await audio.microphone.open()
  } catch {
    void audio.microphone.close()
    void audio.player.close()
    throw new Error(
      "The microphone could not be opened. Allow it in the browser's address bar, or type your answer.",
    )
  }
}

const READY = "I'm ready for the question."
/** If Google never confirms the end of the turn, stop waiting after this. */
const FINISH_TIMEOUT_MS = 6000

export async function startSpokenTurn(
  ticket: VoiceTicket,
  audio: TurnAudio,
  handlers: TurnHandlers,
  options: TurnOptions = {},
): Promise<SpokenTurn> {
  const { player, microphone } = audio
  let phase: TurnPhase = "connecting"
  let answer = ""
  let listeningSince = 0
  let answerSeconds = 0
  let closedByUs = false
  let session: Session | null = null
  let settle: (() => void) | null = null

  const setPhase = (next: TurnPhase) => {
    phase = next
    handlers.onPhase(next)
  }

  const shutDown = () => {
    closedByUs = true
    microphone.send(null)
    session?.close()
    // Hands-free keeps both open for the next question; its owner closes them.
    if (options.keepAudio) return
    void microphone.close()
    // Let the "Thank you" finish before the speaker is closed.
    window.setTimeout(() => void player.close(), player.remainingMs() + 200)
  }

  const fail = (message: string) => {
    if (phase === "done") return
    setPhase("done")
    shutDown()
    handlers.onError(message)
    settle?.()
  }

  const startListening = () => {
    if (phase !== "asking") return
    setPhase("listening")
    listeningSince = performance.now()
    session?.sendRealtimeInput({ activityStart: {} })
    const silence = options.handsFree
      ? createSilenceDetector({
          silenceMs: HANDS_FREE_SILENCE_MS,
          noSpeechMs: HANDS_FREE_NO_SPEECH_MS,
        })
      : null
    let reported = false
    microphone.send((chunk) => {
      session?.sendRealtimeInput({
        audio: { data: toBase64(chunk), mimeType: "audio/pcm;rate=16000" },
      })
      if (!silence || reported || phase !== "listening") return
      const state = silence.push(new Int16Array(chunk))
      if (state === "done") {
        reported = true
        handlers.onSilence?.()
      } else if (state === "no-speech") {
        reported = true
        // Words Google already wrote down are kept: the detector missed the
        // answer, the transcription did not.
        if (answer.trim()) handlers.onSilence?.()
        else fail("Nothing was heard for 20 seconds, so hands-free has paused. Press Answer out loud, or type.")
      }
    })
  }

  let heardFirstSound = false
  const onMessage = (message: LiveServerMessage) => {
    const content = message.serverContent
    if (!content) return
    for (const part of content.modelTurn?.parts ?? []) {
      if (!part.inlineData?.data) continue
      if (!heardFirstSound) {
        heardFirstSound = true
        handlers.onTiming?.("first sound")
      }
      player.play(part.inlineData.data)
    }
    const heard = content.inputTranscription?.text
    if (heard && (phase === "listening" || phase === "finishing")) {
      answer += heard
      handlers.onAnswer(answer.trim())
    }
    if (content.turnComplete) {
      if (phase === "asking") {
        // Listen once the question has actually been heard, not when the last
        // chunk of it arrived - the audio is still playing at that point.
        window.setTimeout(startListening, player.remainingMs())
      } else if (phase === "finishing") {
        setPhase("done")
        shutDown()
        settle?.()
      }
    }
  }

  setPhase("connecting")
  let loaded: typeof import("@google/genai")
  try {
    loaded = await preloadVoice()
  } catch {
    shutDown()
    throw new Error("The voice library could not be downloaded. Type your answer, or try again.")
  }
  handlers.onTiming?.("sdk")
  const { GoogleGenAI, Modality } = loaded
  const ai = new GoogleGenAI({
    apiKey: ticket.token,
    httpOptions: { apiVersion: ticket.apiVersion, baseUrl: ticket.baseUrl },
  })
  try {
    session = await ai.live.connect({
      model: ticket.model,
      // The same settings the token locks. If Google honours the lock these
      // are ignored; if it ever did not, the page still asks for the right thing.
      config: {
        responseModalities: [Modality.AUDIO],
        systemInstruction: ticket.instruction,
        inputAudioTranscription: {},
        outputAudioTranscription: {},
        speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: ticket.voice } } },
        realtimeInputConfig: { automaticActivityDetection: { disabled: true } },
      },
      callbacks: {
        onmessage: onMessage,
        onerror: () => fail("The voice connection failed. Type your answer, or try again."),
        onclose: () => {
          if (!closedByUs) fail("The voice connection closed early. Type your answer, or try again.")
        },
      },
    })
  } catch {
    shutDown()
    throw new Error("Could not reach the interviewer's voice. Type your answer, or try again.")
  }

  handlers.onTiming?.("connected")
  setPhase("asking")
  session.sendClientContent({ turns: [{ role: "user", parts: [{ text: READY }] }], turnComplete: true })

  return {
    finish: () =>
      new Promise((resolve) => {
        const result = () => ({ text: answer.trim(), seconds: answerSeconds })
        if (phase !== "listening") {
          if (phase !== "done") fail("The turn ended before the answer started.")
          resolve(result())
          return
        }
        answerSeconds = Math.round((performance.now() - listeningSince) / 100) / 10
        setPhase("finishing")
        microphone.send(null)
        session?.sendRealtimeInput({ activityEnd: {} })
        // The last words' transcript can arrive after the end of the turn,
        // so wait for Google to close the turn - but not forever.
        const timer = window.setTimeout(() => {
          if (phase === "finishing") {
            setPhase("done")
            shutDown()
          }
          resolve(result())
        }, FINISH_TIMEOUT_MS)
        settle = () => {
          window.clearTimeout(timer)
          resolve(result())
        }
      }),
    cancel: () => {
      if (phase === "done") return
      setPhase("done")
      player.stop()
      shutDown()
    },
  }
}
